import '@ids/style-modal-v19';
import './Modal.css';

import { Ui24Cross } from '@ids/react-icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Action, Content } from '../../global-types/Content';
import { useHandleActionClick } from '../../hooks/useHandleActionClick';
import ActionChoiceModal from './layouts/ActionChoiceModal';
import AfterActionProductPurchaseModal from './layouts/AfterActionProductPurchaseModal';
import ProductPurchaseModal from './layouts/ProductPurchaseModal';
import PurchaseConfirmationModal from './layouts/PurchaseConfirmationModal';

type ModalProps = {
  content: Content;
  viewId: number | undefined;
  dispatchActionEvent: (action: Action, nextContent?: Content | null) => void;
};

function Modal({ content, viewId, dispatchActionEvent }: ModalProps) {
  const modalRef = useRef<HTMLDialogElement | null>(null);
  const view = viewId ? content.contentViews?.find((cv) => cv.contentViewId === viewId) : undefined;
  const [modalOpen, setModalOpen] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const { handleActionClick } = useHandleActionClick(dispatchActionEvent);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.showModal();
      setModalOpen(true);
      document.body.style.overflow = 'hidden';
    } else {
      setModalOpen(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [viewId]);

  const handleCloseModal = useCallback(
    async (action?: Action) => {
      if (!action) return;
      dispatchActionEvent(action);
      await handleActionClick(action);
    },
    [dispatchActionEvent, handleActionClick],
  );

  const closeAction = view?.viewElements?.actions?.[0];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (!disableButton) {
        void handleCloseModal(closeAction);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeAction, disableButton, handleCloseModal]);

  if (!view) return null;

  const sharedProps = { content, viewId, dispatchActionEvent, modalOpen };

  const renderLayout = () => {
    switch (view.viewLayout) {
      case 'afterActionProductPurchaseModal':
        return (
          <AfterActionProductPurchaseModal
            {...sharedProps}
            disableButton={disableButton}
            setDisableButton={setDisableButton}
          />
        );
      case 'productPurchaseModal':
        return (
          <ProductPurchaseModal {...sharedProps} disableButton={disableButton} setDisableButton={setDisableButton} />
        );
      case 'purchaseConfirmationModal':
        return <PurchaseConfirmationModal content={content} viewId={viewId} modalOpen={modalOpen} />;
      case 'actionChoiceModal':
        return <ActionChoiceModal {...sharedProps} />;
      default:
        return null;
    }
  };

  return (
    <dialog
      id="ids-modal-v19"
      className="if modal-v19 align-center"
      ref={modalRef}
      onCancel={(e) => {
        e.preventDefault();
        if (disableButton) {
          return;
        }
        void handleCloseModal(closeAction);
      }}
    >
      <button
        aria-label="close modal-v19"
        className="if button medium tonal modal-v19-close-button"
        id="close-modal-v19"
        type="button"
        disabled={disableButton}
        onClick={() => void handleCloseModal(closeAction)}
      >
        <Ui24Cross />
      </button>
      {renderLayout()}
    </dialog>
  );
}

export default Modal;
